import { FormControl, FormGroup } from '@angular/forms';
import { TravelDirectionEnum } from '@wilson/activities/symbols';
import { Identifiable } from '@wilson/base';
import { ActivityTemplateV2Details } from '../activity-template-v2/activity-template-v2';
import { GeoLocation } from '../locations/geoLocation';
import { SeriesRuleV2 } from '../series-rule-v2/series-rule-v2';
import { WeekDaysEnum } from '../services/weekDays';
import { CreateSeriesRuleV2 } from './create-series-rule-v2';

/**
 * API request DTO for activity template upsert operations.
 *
 * @deprecated Use ActivityModel for FE UI/Forms on future implementations.
 */
export class UpsertActivityTemplateV2 {
  id?: string;
  name!: string;
  startDatetime!: string;
  endDatetime!: string;
  activityCategoryId!: string;
  startLocationId!: string;
  endLocationId!: string;
  recurringOn!: WeekDaysEnum[];
  viaLocationIds?: string[];
  shiftSeriesId?: string;
  serviceSeriesId?: string;
  agreementId?: string | null;
  clientId?: string | null;
  professionId?: string | null;
  numberOfWagons?: number | null;
  tonnage?: number | null;
  travelDirection?: TravelDirectionEnum | null;
  vehicleEmissionClassId?: number | null;
  organizationalUnitTransportGoodsId?: string | null;
  cargo?: string | null;
  track?: string | null;
  externalNumber?: string | null;
  note?: string | null;
  orderNumber?: string | null;
  validateRoute?: boolean | null;
  trainNumber?: string | null;
  wagonNumber?: string | null;
  locomotiveNumber?: string | null;
  seriesRules?: CreateSeriesRuleV2[];
  qualificationIds?: string[] | null;
  qualificationProfileIds?: string[] | null;

  /**
   * Creates an UpsertActivityTemplateV2 from API response data.
   */
  static fromApiResponse(
    template: ActivityTemplateV2Details,
    shiftSeriesRules: SeriesRuleV2[],
  ): UpsertActivityTemplateV2 {
    const seriesRules = template.seriesRules
      .filter(({ type }) => type !== 'exclude')
      .map((seriesRule) => {
        const shiftSeriesRuleOnTheSamePeriod = shiftSeriesRules.find(
          (shiftSeriesRule) =>
            SeriesRuleV2.areSamePeriod(shiftSeriesRule, seriesRule),
        );

        return CreateSeriesRuleV2.fromSeriesRuleV2({
          ...seriesRule,
          id: shiftSeriesRuleOnTheSamePeriod?.id ?? seriesRule.id,
        });
      });

    return {
      id: template.id,
      // In the BE, the name is required to be a string, but it can be empty
      name: template.name || null,
      startDatetime: template.startDatetime,
      endDatetime: template.endDatetime,
      activityCategoryId: template.activityCategoryId,
      viaLocationIds: template.viaLocationIds,
      shiftSeriesId: template.shiftSeriesId ?? undefined,
      serviceSeriesId: template.serviceSeriesId ?? undefined,
      startLocationId: template.startLocationId,
      endLocationId: template.endLocationId,
      agreementId: template.agreementId,
      professionId: template.professionId,
      cargo: template.cargo ?? undefined,
      track: template.track ?? undefined,
      externalNumber: template.externalNumber ?? undefined,
      note: template.note ?? undefined,
      orderNumber: template.orderNumber ?? undefined,
      wagonNumber: template.wagonNumber ?? undefined,
      locomotiveNumber: template.locomotiveNumber ?? undefined,
      seriesRules,
      recurringOn: template.recurringOn,
      qualificationIds: template.qualificationIds,
      qualificationProfileIds: template.qualificationProfileIds,
      numberOfWagons: template.numberOfWagons,
      tonnage: template.tonnage,
      vehicleEmissionClassId: template.vehicleEmissionClassId,
      travelDirection: template.travelDirection,
      validateRoute: template.validateRoute,
      trainNumber: template.trainNumber,
      clientId: template.clientId,
      organizationalUnitTransportGoodsId:
        template.organizationalUnitTransportGoodsId ?? undefined,
    } as UpsertActivityTemplateV2;
  }

  /**
   * Prepares the template for API submission.
   * Strips id if it's a temp id (new-* prefix), keeps id for existing templates.
   */
  static toApiRequest(
    template: UpsertActivityTemplateV2,
  ): UpsertActivityTemplateV2 {
    const { name, validateRoute, ...templateWithNoName } = template;

    if (templateWithNoName.id && Identifiable.isNew(templateWithNoName.id)) {
      delete templateWithNoName.id;
    }

    const result = {
      // In the BE, the name is required to be a string, but it can be empty
      name: name ?? '',
      validateRoute: validateRoute ?? false,
      ...templateWithNoName,
    };

    // Remove undefined values
    return Object.fromEntries(
      Object.entries(result).filter(([_, value]) => value !== undefined),
    ) as unknown as UpsertActivityTemplateV2;
  }
}

export interface ActivityTemplateV2FormValue {
  general: {
    activityCategoryId: string;
    name: string;
  };
  timeAndLocation: {
    startDateTime: Date | null;
    endDateTime: Date | null;
    startLocation: GeoLocation | null;
    endLocation: GeoLocation | null;
  };
  shiftValidation: {
    professionId: string | null;
    clientId: string | null;
    agreementId: string | null;
    qualificationIds: string[];
  };
  additionalInformation: {
    numberOfWagons: number | null;
    tonnage: number | null;
    vehicleEmissionClassId: number | null;
    travelDirection: TravelDirectionEnum | null;
    organizationalUnitTransportGoodsId: string | null;
    cargo: string | null;
    track: string | null;
    externalNumber: string | null;
    orderNumber: string | null;
    wagonNumber: string | null;
    locomotiveNumber: string | null;
  };
  note: {
    note: string | null;
  };
}

export interface ActivityTemplateV2FormControls {
  general: FormGroup<{
    activityCategoryId: FormControl<string>;
    name: FormControl<string>;
  }>;
  timeAndLocation: FormGroup<{
    startDateTime: FormControl<Date | null>;
    endDateTime: FormControl<Date | null>;
    startLocation: FormControl<GeoLocation | null>;
    endLocation: FormControl<GeoLocation | null>;
  }>;
  shiftValidation: FormGroup<{
    professionId: FormControl<string | null>;
    clientId: FormControl<string | null>;
    agreementId: FormControl<string | null>;
    qualificationIds: FormControl<string[]>;
  }>;
  additionalInformation: FormGroup<{
    numberOfWagons: FormControl<number | null>;
    tonnage: FormControl<number | null>;
    vehicleEmissionClassId: FormControl<number | null>;
    travelDirection: FormControl<TravelDirectionEnum | null>;
    organizationalUnitTransportGoodsId: FormControl<string | null>;
    cargo: FormControl<string | null>;
    track: FormControl<string | null>;
    externalNumber: FormControl<string | null>;
    orderNumber: FormControl<string | null>;
    wagonNumber: FormControl<string | null>;
    locomotiveNumber: FormControl<string | null>;
  }>;
  note: FormGroup<{
    note: FormControl<string | null>;
  }>;
}
